/* eslint max-lines: off */

import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import scrollToElement from 'scroll-to-element';
import './page_nav.scss';
import '../../entity/entity.scss';
import { NavItem } from './NavItem';
import { NewNavContainer } from './NewNavContainer';
import { doctrineAnalytics } from '../../utils/analyticsUtils';
import { InjectedIntlProps } from '../../doctrine.types';

export type OutlineItem = {
  id: string;
  text?: string;
  textId?: string;
  type?: string;
  exist: boolean;
};

type Props = {
  outline: Array<OutlineItem>;
  newNav: boolean;
} & InjectedIntlProps;

type State = {
  showNav: boolean;
  outlineUpdated: Array<OutlineItem>;
  hasLegalIntelligence: boolean;
  hasInformations: boolean;
};

export class RawPageNav extends React.Component<Props, State> {
  static defaultProps = {
    newNav: false,
  };

  state: State = {
    showNav: true,
    outlineUpdated: this.props.outline,
    hasLegalIntelligence: false,
    hasInformations: false,
  };

  async componentDidMount() {
    window.addEventListener('createNav', this.createNav as EventListener);
    window.addEventListener('showNav', this.showNav as EventListener);
    window.addEventListener('scroll', this.onScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('createNav', this.createNav as EventListener);
    window.removeEventListener('showNav', this.showNav as EventListener);
    window.removeEventListener('scroll', this.onScroll);
  }

  createNav = (event: CustomEvent) => {
    const { outline } = this.props;
    const index = outline.findIndex(
      navElement => navElement.id === event.detail,
    );
    if (index !== -1) {
      this.setState(
        ({ outlineUpdated, hasLegalIntelligence, hasInformations }) => {
          const outlineCopy = [...outlineUpdated];
          outlineCopy[index].exist = true;
          return {
            outlineUpdated: outlineCopy,
            hasLegalIntelligence:
              hasLegalIntelligence ||
              outlineCopy[index].type === 'legal intelligence',
            hasInformations:
              hasInformations || outlineCopy[index].type === 'informations',
          };
        },
      );
    }
  };

  showNav = (event: CustomEvent) => {
    this.setState({ showNav: event.detail });
  };

  onScroll = () => {
    if (this.state.showNav) {
      const container = window.document.querySelector(
        `[data-reference=page]`,
      ) as HTMLElement;
      const containerHeight = container.offsetHeight;
      const nav = window.document.getElementById('page-nav');
      if (nav) {
        const navHeight = nav.offsetHeight;
        const bottomMargin = 15;
        const topPadding = 10;
        if (
          window.pageYOffset >
          containerHeight + bottomMargin - navHeight - topPadding
        ) {
          nav.classList.add('entity__bottom');
          nav.classList.remove('page-nav');
        } else {
          nav.classList.add('page-nav');
          nav.classList.remove('entity__bottom');
        }
      }
    }
  };

  getNavItemTitle = (navItem: OutlineItem) => {
    const { intl } = this.props;
    return (
      navItem.text ||
      (navItem.textId
        ? intl.formatMessage({
            id: navItem.textId,
          })
        : '')
    );
  };

  render() {
    const { newNav } = this.props;
    const {
      outlineUpdated,
      hasLegalIntelligence,
      showNav,
      hasInformations,
    } = this.state;
    return (
      showNav && (
        <div className="page-nav" id="page-nav">
          <NewNavContainer newNav={newNav}>
            {hasInformations && (
              <>
                <div className="page-nav__title">
                  <FormattedMessage id="components/page-nav/PageNav.tsx/1" />
                </div>
                <div className="page-nav__domains">
                  {outlineUpdated.map(
                    navItem =>
                      navItem.type === 'informations' &&
                      navItem.exist && (
                        <NavItem
                          key={navItem.id}
                          item={navItem}
                          handleClick={() =>
                            goToId(navItem.id, this.getNavItemTitle(navItem))
                          }
                          newNav={newNav}
                        />
                      ),
                  )}
                </div>
              </>
            )}
            {hasLegalIntelligence && (
              <>
                <div className="page-nav__title">
                  <FormattedMessage id="components/page-nav/PageNav.tsx/2" />
                </div>
                <div className="page-nav__domains">
                  {outlineUpdated.map(
                    navItem =>
                      navItem.type === 'legal intelligence' &&
                      navItem.exist && (
                        <NavItem
                          key={navItem.id}
                          item={navItem}
                          handleClick={() =>
                            goToId(navItem.id, this.getNavItemTitle(navItem))
                          }
                          newNav={newNav}
                        />
                      ),
                  )}
                </div>
              </>
            )}
            {outlineUpdated.map(
              navItem =>
                !navItem.type &&
                navItem.exist && (
                  <NavItem
                    key={navItem.id}
                    item={navItem}
                    handleClick={() =>
                      goToId(navItem.id, this.getNavItemTitle(navItem))
                    }
                    newNav={newNav}
                  />
                ),
            )}
          </NewNavContainer>
        </div>
      )
    );
  }
}

function goToId(id: string, title: string) {
  scrollToElement(`#${id}`, {
    offset: -130,
    ease: 'out-quart',
    duration: 500,
  });
  doctrineAnalytics('PageNav NavItemClicked', {
    id,
    title,
  });
}

export const PageNav = injectIntl(RawPageNav);
